import React, { useState, useRef, useEffect } from 'react';
import './index.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { PopupModal } from 'react-calendly';
import { knowledgeBase, fallbackResponse } from './knowledgeBase';

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "Hi! I'm your virtual assistant. You can ask me anything about our services, pricing, or process. How can I help?",
            sender: 'bot'
        }
    ]);
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    useEffect(() => {
        scrollToBottom();
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [messages, isOpen]);

    const findResponse = (text) => {
        const lowerText = text.toLowerCase();

        // Find best match based on keywords (using word boundaries for accuracy)
        const match = knowledgeBase.find(item =>
            item.keywords.some(keyword => {
                // Escape special regex characters in keyword just in case
                const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`\\b${escapedKeyword}\\b`, 'i');
                return regex.test(lowerText);
            })
        );

        if (match) {
            return {
                text: match.response,
                link: match.link,
                linkText: match.linkText,
                action: match.action
            };
        }

        return {
            text: fallbackResponse,
            action: "OPEN_CALENDLY" // Offer call on fallback
        };
    };

    const handleSendMessage = (text) => {
        if (!text.trim()) return;

        // Add user message
        const userMsg = { id: Date.now(), text: text, sender: 'user' };
        setMessages(prev => [...prev, userMsg]);
        setInputValue('');

        // Process response
        const responseData = findResponse(text);

        // Simulate typing delay
        setTimeout(() => {
            const botMsg = {
                id: Date.now() + 1,
                text: responseData.text,
                sender: 'bot',
                link: responseData.link,
                linkText: responseData.linkText,
                action: responseData.action
            };
            setMessages(prev => [...prev, botMsg]);

            if (responseData.action === "OPEN_CALENDLY") {
                setTimeout(() => setIsCalendlyOpen(true), 800);
            }
        }, 600);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSendMessage(inputValue);
        }
    };

    const handleOptionClick = (option) => {
        handleSendMessage(option.label); // Treat option click as user sending that text
    };

    const handleNavigate = (link) => {
        setIsOpen(false);
        navigate(link);
    };

    const suggestedOptions = [
        { label: "Pricing info" },
        { label: "Services offered" },
        { label: "Book a call" }
    ];

    return (
        <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>

            {/* Calendly Modal */}
            <PopupModal
                url="https://calendly.com/vermresearch-proton/30min"
                onModalClose={() => setIsCalendlyOpen(false)}
                open={isCalendlyOpen}
                rootElement={document.getElementById("root")}
            />

            {/* Chat Window */}
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <h3>Assistant</h3>
                        <button onClick={() => setIsOpen(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="chat-messages">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`message ${msg.sender}`}>
                                <div className="bubble">
                                    {msg.text}
                                    {msg.link && (
                                        <button
                                            className="chat-link-btn"
                                            onClick={() => handleNavigate(msg.link)}
                                        >
                                            {msg.linkText}
                                        </button>
                                    )}
                                    {msg.action === "OPEN_CALENDLY" && msg.sender === 'bot' && !msg.link && (
                                        <button
                                            className="chat-link-btn"
                                            onClick={() => setIsCalendlyOpen(true)}
                                        >
                                            Open Calendar
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="chat-input-area">
                        {/* Suggested Chips above input if it's the start or just helpful */}
                        <div className="suggestions">
                            {suggestedOptions.map((opt, idx) => (
                                <button key={idx} onClick={() => handleOptionClick(opt)}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        <div className="input-row">
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Type your question..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <button onClick={() => handleSendMessage(inputValue)}>
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Bubble */}
            <button className="chat-bubble-btn" onClick={() => setIsOpen(!isOpen)}>
                <FontAwesomeIcon icon={isOpen ? faTimes : faCommentDots} size="2x" />
            </button>
        </div>
    );
};

export default Chatbot;
